import { useEffect, useRef, useState } from "react";
import { clampElapsed } from "./replay";
export function useReplay(durationMs) {
    const [elapsedMs, setElapsedMs] = useState(0);
    const [status, setStatus] = useState("idle");
    const [playbackRate, setPlaybackRateState] = useState(1);
    const elapsedRef = useRef(0);
    const playbackRateRef = useRef(1);
    useEffect(() => {
        if (status !== "playing") {
            return;
        }
        let frameId = 0;
        let lastFrameAt = performance.now();
        const frame = (frameAt) => {
            const deltaMs = (frameAt - lastFrameAt) * playbackRateRef.current;
            lastFrameAt = frameAt;
            const nextElapsed = clampElapsed(elapsedRef.current + deltaMs, durationMs);
            elapsedRef.current = nextElapsed;
            setElapsedMs(nextElapsed);
            if (nextElapsed >= durationMs) {
                setStatus("complete");
                return;
            }
            frameId = requestAnimationFrame(frame);
        };
        frameId = requestAnimationFrame(frame);
        return () => cancelAnimationFrame(frameId);
    }, [durationMs, status]);
    const play = () => {
        if (elapsedRef.current >= durationMs) {
            elapsedRef.current = 0;
            setElapsedMs(0);
        }
        setStatus("playing");
    };
    const pause = () => {
        setStatus((current) => (current === "idle" ? "idle" : "paused"));
    };
    const restart = (autoplay = false) => {
        elapsedRef.current = 0;
        setElapsedMs(0);
        setStatus(autoplay ? "playing" : "idle");
    };
    const seek = (nextElapsedMs) => {
        const nextElapsed = clampElapsed(nextElapsedMs, durationMs);
        elapsedRef.current = nextElapsed;
        setElapsedMs(nextElapsed);
        setStatus((current) => {
            if (nextElapsed >= durationMs) {
                return "complete";
            }
            return current === "playing" ? "playing" : nextElapsed === 0 ? "idle" : "paused";
        });
    };
    const setPlaybackRate = (rate) => {
        playbackRateRef.current = rate;
        setPlaybackRateState(rate);
    };
    return {
        elapsedMs,
        status,
        playbackRate,
        play,
        pause,
        restart,
        seek,
        setPlaybackRate
    };
}
